import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import './ProductsPages.css';
import ProductsBeHeader from '../../components/ProductsBeHeader';
import TitleAndContentCard from '../../components/TitleAndContentCard';
import CheckCircle from '../../icons/CheckCircle';
import WhyChooseOurPms from '../../components/WhyChooseOurPms';
import { useTranslation } from 'react-i18next';
import useLanguagePrefix from '../../LanguagePrefix';

function BookingEngine() {
    const { t } = useTranslation();
    const langPrefix = useLanguagePrefix();
    const features = [
        { title: t('Real-Time Availability'), content: t('Syncs instantly with your PMS and Channel Manager—no delays, no double bookings.') },
        { title: t('Mobile-Optimized Design'), content: t('Designed for a smooth, high-converting booking experience on any device.') },
        { title: t('Brand Customization'), content: t('Customize the look and feel to match your hotel’s website and visual identity.') },
        { title: t('Multi-Language & Currency'), content: t('Cater to global guests with full support for multiple languages and currencies.') },
    ];

    const roomPlanningExpandableCards = [
        { title: t("Centralized Dashboard"), content: t("Manage reservations, availability, and guest data in one place.") },
        { title: t("Housekeeping & Task Management"), content: t("Manage reservations, availability, and guest data in one place.") },
        { title: t("Channel Manager"), content: t("Manage reservations, availability, and guest data in one place.") },
    ];
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const [activeIndex, setActiveIndex] = useState(0);

    const handleActiveIndex = (index) => {
        setActiveIndex(index);
    }

    return (
        <div>
            <Helmet>
                <title>Hotel Booking Engine & Hotel Reservation System - Prostay</title>
                <meta name="description" content="Increase direct bookings with a real-time, mobile-friendly hotel booking engine that’s integrated with your PMS and payment gateways." />
                <link rel="canonical" href={"https://www.prostay.com" + langPrefix + "/booking-engine/"} />

                <link rel="alternate" href="https://www.prostay.com/en/booking-engine/" />
                <link rel="alternate" href="https://www.prostay.com/es/booking-engine/" />

                <meta property="og:title" content="Hotel Booking Engine & Hotel Reservation System - Prostay" />
                <meta property="og:description" content="Increase direct bookings with a real-time, mobile-friendly hotel booking engine that’s integrated with your PMS and payment gateways." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={"https://www.prostay.com" + langPrefix + "/booking-engine/"} />
                <meta property="og:image" content='https://prostay.com/images/ogImage.png' />

                <meta property="og:site_name" content="Prostay" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Hotel Booking Engine & Hotel Reservation System - Prostay" />
                <meta name="twitter:description" content="Increase direct bookings with a real-time, mobile-friendly hotel booking engine that’s integrated with your PMS and payment gateways." />
                <meta name="twitter:image" content='https://prostay.com/images/ogImage.png' />
                <meta name="twitter:site" content="@prostaypms" />
                <meta name="twitter:creator" content="@prostaypms" />


            </Helmet>
            <div className='product-page'>
                <ProductsBeHeader />
                <div className='d-flex justify-content-center product-page-content'>
                    <div className='page-content'>
                        <div className='d-flex justify-content-between py-2 py-md-5' style={{ flexWrap: 'wrap' }}>
                            <div className='col-12 col-md-5'>
                                <h2>{t('Hotel Booking Engine Built to Convert, Tailored to Your Brand')}</h2>
                            </div>
                            <div className='col-12 col-md-5'>
                                <p className='body-large'>
                                    {t('From customizable room descriptions and image galleries to upsells and promotional codes, everything is tailored to fit your property’s identity. Whether on desktop, tablet, or mobile, your guests enjoy a fast, intuitive, and secure booking experience.')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='d-flex features-carousel-wrapper'>
                    <div className='d-flex gap-3 features-carousel pb-5'>
                        {
                            features.map((feature, index) =>
                                <TitleAndContentCard title={feature.title} content={feature.content} width={384} height={178} onClick={() => handleActiveIndex(index)} />
                            )
                        }
                    </div>
                </div>
                <div className='d-flex justify-content-center py-2 py-md-5' style={{ flexWrap: 'wrap' }}>
                    <div className='page-content d-flex justify-content-between gap-4'>
                        <div className='col-12 col-md-4'>
                            <div className={selectedTabIndex == 0 ? 'tab-btn focused' : 'tab-btn'} onClick={() => setSelectedTabIndex(0)}>
                                <label className='label-medium'> {t('Brand Customization')} </label>
                                {
                                    selectedTabIndex == 0 &&
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14 5L21 12M21 12L14 19M21 12H3" stroke="#1643E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                }
                            </div>
                            <div className={selectedTabIndex == 1 ? 'tab-btn focused' : 'tab-btn'} onClick={() => setSelectedTabIndex(1)}>
                                <label className='label-medium'> {t('Secure Payments')} </label>
                                {
                                    selectedTabIndex == 1 &&
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14 5L21 12M21 12L14 19M21 12H3" stroke="#1643E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                }
                            </div>
                            <div className={selectedTabIndex == 2 ? 'tab-btn focused' : 'tab-btn'} onClick={() => setSelectedTabIndex(2)}>
                                <label className='label-medium'> {t('Analytics & Tracking')} </label>
                                {
                                    selectedTabIndex == 2 &&
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14 5L21 12M21 12L14 19M21 12H3" stroke="#1643E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                }
                            </div>
                            <div className={selectedTabIndex == 3 ? 'tab-btn focused' : 'tab-btn'} onClick={() => setSelectedTabIndex(3)}>
                                <label className='label-medium'> {t('Add-ons & Upsells')} </label>
                                {
                                    selectedTabIndex == 3 &&
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14 5L21 12M21 12L14 19M21 12H3" stroke="#1643E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                }
                            </div>
                            <div className={selectedTabIndex == 4 ? 'tab-btn focused' : 'tab-btn'} onClick={() => setSelectedTabIndex(4)}>
                                <label className='label-medium'> {t('Rate Plans & Promotions')} </label>
                                {
                                    selectedTabIndex == 4 &&
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14 5L21 12M21 12L14 19M21 12H3" stroke="#1643E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                }
                            </div>
                        </div>
                        <div className='col tab-content'>
                            {
                                selectedTabIndex == 0 &&
                                <>
                                    <h4>{t('Brand Customization')}</h4>
                                    <img src='/images/brandcustomization.svg' alt={"Room planning"} />
                                    <p>
                                        {t('Your Booking Engine should feel like part of your website—not a third-party plug-in. Prostay allows full visual customization, from fonts and colors to layout and tone, ensuring consistency with your brand identity.')}
                                    </p>
                                    <p>
                                        {t('Guests enjoy a seamless transition from your site into the booking process without losing trust or aesthetic continuity.')}
                                    </p>
                                    <h5 className='my-4'>{t('Room Planning')}</h5>
                                    <div className='d-flex align-items-center gap-2'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Match your website’s branding')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Custom colors, fonts, images, and language')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Seamless user experience')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Eliminate visual disconnect')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Boost booking confidence and brand loyalty')}</p>
                                    </div>
                                </>
                            }
                            {
                                selectedTabIndex == 1 &&
                                <>
                                    <h4>{t('Secure Payments')}</h4>
                                    {/*<img src='/images/room-planning.png' alt={"Room planning"} /> */}
                                    <p>
                                        {t('With integrated payment gateways and PCI-compliant architecture, Prostay makes it simple and safe to collect payments, deposits, or credit card guarantees at the time of booking.')}
                                    </p>
                                    <p>
                                        {t('Whether you’re charging partial, full, or flexible rates, guests can complete their reservations with confidence.')}
                                    </p>
                                    <h5 className='my-4'>{t('Room Planning')}</h5>
                                    <div className='d-flex align-items-center gap-2'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('PCI-compliant payment processing')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Flexible payment models (guarantees, deposits)')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Integration with major payment gateways')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Secure card tokenization')}</p>
                                    </div>
                                </>
                            }
                            {
                                selectedTabIndex == 2 &&
                                <>
                                    <h4>{t('Analytics & Tracking')}</h4>
                                    {/* <img src='/images/room-planning.png' alt={"Room planning"} /> */}
                                    <p>
                                        {t('Prostay’s Booking Engine makes it easy to measure performance and optimize your marketing efforts with full support for third-party analytics and tracking tools.')}
                                    </p>
                                    <p>
                                        {t('Seamlessly integrate platforms like Google Analytics, Google Tag Manager, Meta Pixel (Facebook), and other marketing tools to monitor conversions, track campaign performance, and refine your digital strategy.')}
                                    </p>
                                    <p>
                                        {t('With proper tagging and event tracking, you’ll gain actionable insights into guest behavior—right from search to booking confirmation.')}
                                    </p>
                                    <h5 className='my-4'>{t('Room Planning')}</h5>
                                    <div className='d-flex align-items-center gap-2'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Easy integration with Google Analytics & GTM')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Facebook/Meta Pixel support for campaign tracking')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Monitor conversions and ROI from ads')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Add custom scripts for additional tracking needs')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Make data-driven marketing decisions')}</p>
                                    </div>
                                </>
                            }
                            {
                                selectedTabIndex == 3 &&
                                <>
                                    <h4>{t('Add-ons & Upsells')}</h4>
                                    {/*  <img src='/images/room-planning.png' alt={"Room planning"} /> */}
                                    <p>
                                        {t('Boost your revenue per booking by offering guests optional add-ons during the reservation process. Prostay allows you to showcase room upgrades, late check-out, airport transfers, dining packages, spa services, and more—all during checkout.')}
                                    </p>
                                    <p>
                                        {t('Each upsell is customizable, and revenue is tracked directly in the system.')}
                                    </p>
                                    <h5 className='my-4'>{t('Room Planning')}</h5>
                                    <div className='d-flex align-items-center gap-2'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Add room upgrades and services')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Upsell at the point of booking')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Customize per room type or date range')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Track upsell revenue in reports')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Improve guest satisfaction with personalization')}</p>
                                    </div>
                                </>
                            }
                            {
                                selectedTabIndex == 4 &&
                                <>
                                    <h4>{t('Rate Plans & Promotions')}</h4>
                                    {/* <img src='/images/room-planning.png' alt={"Room planning"} /> */}
                                    <p>
                                        {t('Prostay makes it easy to create and manage a wide variety of rate plans. From standard BAR and non-refundable rates to corporate, group, and promotional rates, every structure is supported.')}
                                    </p>
                                    <p>
                                        {t('You can also configure packages that include breakfast, tours, or activities, and apply custom discounts or promo codes to boost direct bookings and drive specific campaigns.')}
                                    </p>
                                    <h5 className='my-4'>{t('Room Planning')}</h5>
                                    <div className='d-flex align-items-center gap-2'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Full support for all rate types')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Customizable packages and inclusions')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Easy creation of promo codes and discounts')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Derived and override rate logic')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Ideal for seasonal, corporate, and loyalty campaigns')}</p>
                                    </div>
                                </>
                            }
                        </div>
                    </div>
                </div>
                <div className='d-flex justify-content-center mt-5 d-none d-md-flex'>
                    <div className='page-content d-flex w-100 justify-content-center align-items-center' style={{ textAlign: "center" }}>
                        <div className='col-lg-6 d-flex flex-column align-items-center'>
                            <h3 style={{ textAlign: "center", maxWidth: '640px' }}>{t('Hotel Reservation System Seamlessly Connected to Everything That Matters')}</h3>
                            <p className='body-large' style={{ textAlign: "center", maxWidth: 830 }}>
                                {t('Prostay’s Booking Engine is fully integrated with your PMS, Channel Manager, and guest messaging tools—ensuring every reservation flows smoothly into your operations without the need for manual work.')}
                            </p>
                        </div>
                    </div>
                </div>
                <div className='d-flex justify-content-center bottom-padding'>
                    <div className='page-content '>
                        <div className='white-card p-3 p-md-5 d-flex justify-content-between mt-3 mt-lg-5 gap-4 gap-lg-1' style={{ flexWrap: "wrap" }}>
                            <div className='col-12 col-lg-5'>
                                <h4>{t('Turn Lookers Into Bookers')}</h4>
                                <p className='mt-4'>
                                    {t('Prostay’s Booking Engine isn’t a standalone widget—it’s a fully integrated part of your operations. Every reservation flows directly into your PMS, updates your Channel Manager in real time, and triggers automated guest communication through Prostay Nexus.')}
                                </p>
                                <p>
                                    {t('No manual input. No delays. Just a smooth, end-to-end booking process that keeps your team efficient and your guests happy.')}
                                </p>
                                <a href={`${langPrefix}/request-demo/`} style={{ textDecoration: 'none' }}><button className='light-blue-filled-btn-small mt-5'>
                                    {t('Request Demo')}
                                    <svg width="14" height="12" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M13 5.99992H1Z" fill="white" />
                                        <path d="M8.33333 1.33325L13 5.99992M13 5.99992L8.33333 10.6666M13 5.99992H1" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button></a>
                            </div>
                            <div className='col-12 col-lg-6'>
                                <img src='/images/bookers.svg' style={{ maxWidth: "100%" }} alt={"Room planning"} />
                            </div>
                        </div>
                    </div>
                </div>
                <WhyChooseOurPms
                    headerTitle="Take Charge with Hotel PMS Today!"
                    headerDescription="Cutting-edge Property Management System designed to revolutionize the way hotels, and resorts from boutique to large-scale, operate and serve their guests."
                    sectionTitle={t("Why Do Smart Hoteliers Choose Our Hotel Reservation System?")}
                    sectionDescription={t("Have questions about how Prostay’s Booking Engine works? From setup and payments to branding and performance, here are the answers to the most common questions we hear from hoteliers and property managers.")}
                    cards={[
                        { title: t("Does Prostay charge commission on Booking Engine bookings?"), description: t("No, never. All bookings made through the Prostay Booking Engine are 100% commission-free. You keep the full revenue from every direct reservation, helping you reduce dependency on OTAs and increase your profit margins.") },
                        { title: t("Can I embed the Booking Engine directly into my website?"), description: t("Yes. Prostay’s Booking Engine can be fully embedded into your website or launched as a standalone link. Our team will assist you with the setup to ensure it looks and feels like a natural part of your site.") },
                        { title: t("Does the Booking Engine support multiple properties or room types?"), description: t("Absolutely. You can showcase multiple properties (if applicable), various room types, occupancy rules, and rate plans—fully customizable to your needs.") },
                        { title: t("How do payments work in the Booking Engine?"), description: t("You can integrate your preferred payment gateway to securely accept credit cards, deposits, or full payments directly through the Booking Engine. Prostay supports multiple payment options and currencies.") },
                        { title: t("Can I track conversions and booking performance?"), description: t("Yes. Prostay provides built-in reporting and also supports integration with tools like Google Analytics and Facebook Pixel, so you can track performance and optimize your direct booking strategy.") }
                    ]}
                />
            </div>
        </div>
    );
}

export default BookingEngine;
